import { desembrulhar, fail, ok, type Result } from '../result.js';

/** `estrangeiro`: registro no país de origem (fase 2, W5), para parceiro com país diferente de BR. */
export type TipoDocumento = 'cpf' | 'cnpj' | 'estrangeiro';

/** Tamanho máximo do registro no país de origem (o mesmo do diálogo "Nova pessoa" do sistema antigo). */
export const MAX_DOCUMENTO_ESTRANGEIRO = 30;

/** País padrão do parceiro: com ele o documento é CPF ou CNPJ. */
export const PAIS_PADRAO = 'BR';

/**
 * Chave de unicidade do documento (fase 2, W5): sem espaço e sem diferença de maiúsculas. Para CPF e CNPJ (só dígitos) é o
 * próprio valor. A mesma conta do índice `cadastros_parceiros_documento_chave` (cadastros 0010).
 */
export const chaveDeDocumento = (valor: string) => valor.replace(/ /g, '').toUpperCase();

/**
 * Registro estrangeiro só com ASCII visível (letras sem acento, dígitos e pontuação): a chave em maiúsculas do JS e a do
 * Postgres (`upper`, que depende do locale fora do ASCII) ficam iguais, e não entra controle nem caractere invisível.
 */
const ASCII_VISIVEL = /^[\x20-\x7e]+$/;


function cpfValido(d: string): boolean {
  if (!/^\d{11}$/.test(d) || /^(\d)\1{10}$/.test(d)) return false;
  const dv = (base: string, pesoInicial: number) => {
    let soma = 0;
    for (let i = 0; i < base.length; i++) soma += Number(base[i]) * (pesoInicial - i);
    const r = (soma * 10) % 11;
    return r === 10 ? 0 : r;
  };
  return dv(d.slice(0, 9), 10) === Number(d[9]) && dv(d.slice(0, 10), 11) === Number(d[10]);
}

function cnpjValido(d: string): boolean {
  if (!/^\d{14}$/.test(d) || /^(\d)\1{13}$/.test(d)) return false;
  const dv = (base: string) => {
    const pesos = base.length === 12 ? [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2] : [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
    const soma = pesos.reduce((acc, p, i) => acc + p * Number(base[i]), 0);
    const r = soma % 11;
    return r < 2 ? 0 : 11 - r;
  };
  return dv(d.slice(0, 12)) === Number(d[12]) && dv(d.slice(0, 13)) === Number(d[13]);
}

/**
 * CPF ou CNPJ, guardado só com dígitos. Fase 2 (W5): com país diferente de BR, o "registro no país de origem", texto de até
 * 30 caracteres guardado como digitado (bordas cortadas, espaços repetidos viram um), único pela `chave` (sem espaço e sem
 * diferença de maiúsculas).
 */
export class Documento {
  private constructor(
    public readonly tipo: TipoDocumento,
    public readonly valor: string,
  ) {}

  /** `pais` ausente ou BR: CPF ou CNPJ. Outro país: registro estrangeiro. */
  static tryCreate(bruto: string, pais?: string | null): Result<Documento> {
    if (pais && pais.toUpperCase() !== PAIS_PADRAO) return Documento.tryCreateEstrangeiro(bruto);
    const d = (bruto ?? '').replace(/\D/g, '');
    if (d.length === 11) return cpfValido(d) ? ok(new Documento('cpf', d)) : fail('documento.cpf_invalido', 'CPF inválido');
    if (d.length === 14) return cnpjValido(d) ? ok(new Documento('cnpj', d)) : fail('documento.cnpj_invalido', 'CNPJ inválido');
    return fail('documento.invalido', 'Documento deve ser CPF ou CNPJ');
  }

  /** Registro no país de origem: até 30 caracteres de ASCII visível, com ao menos uma letra ou um dígito. */
  static tryCreateEstrangeiro(bruto: string): Result<Documento> {
    const v = String(bruto ?? '').trim().replace(/[ \t]+/g, ' ');
    if (!v || !/[A-Za-z0-9]/.test(v)) return fail('documento.estrangeiro_invalido', 'Informe o registro no país de origem');
    if (v.length > MAX_DOCUMENTO_ESTRANGEIRO) return fail('documento.estrangeiro_longo', `Registro no país de origem com até ${MAX_DOCUMENTO_ESTRANGEIRO} caracteres`);
    if (!ASCII_VISIVEL.test(v)) return fail('documento.estrangeiro_invalido', 'Use letras sem acento, números e pontuação no registro no país de origem');
    return ok(new Documento('estrangeiro', v));
  }

  static create(bruto: string, pais?: string | null): Documento {
    return desembrulhar(Documento.tryCreate(bruto, pais));
  }

  /** Reconstrói o gravado sem revalidar (regra nova não congela registro antigo). */
  static reidratar(valor: string, pais?: string | null): Documento {
    if (pais && pais.toUpperCase() !== PAIS_PADRAO) return new Documento('estrangeiro', valor);
    return new Documento(valor.length === 14 ? 'cnpj' : 'cpf', valor);
  }

  /** Chave de unicidade: sem espaço e sem diferença de maiúsculas (CPF e CNPJ: o próprio valor). */
  get chave(): string {
    return chaveDeDocumento(this.valor);
  }

  formatado(): string {
    const v = this.valor;
    if (this.tipo === 'estrangeiro') return v;
    return this.tipo === 'cpf'
      ? `${v.slice(0, 3)}.${v.slice(3, 6)}.${v.slice(6, 9)}-${v.slice(9)}`
      : `${v.slice(0, 2)}.${v.slice(2, 5)}.${v.slice(5, 8)}/${v.slice(8, 12)}-${v.slice(12)}`;
  }
}
